import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'magtic_a11y';

const defaultSettings = {
  fontSize: 'normal',      // 'normal' | 'large' | 'larger'
  contrast: 'normal',     // 'normal' | 'high'
  reduceMotion: false,
  focusVisible: false,
  underlineLinks: false,
};

const AccessibilityContext = createContext(null);

export function AccessibilityProvider({ children }) {
  const [settings, setSettingsState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultSettings, ...parsed };
      }
    } catch (e) {}
    return defaultSettings;
  });

  const applyToDocument = useCallback((s) => {
    const root = document.documentElement;
    root.classList.remove(
      'a11y-font-large', 'a11y-font-larger',
      'a11y-contrast-high',
      'a11y-reduce-motion',
      'a11y-focus-visible',
      'a11y-underline-links'
    );
    if (s.fontSize === 'large') root.classList.add('a11y-font-large');
    if (s.fontSize === 'larger') root.classList.add('a11y-font-larger');
    if (s.contrast === 'high') root.classList.add('a11y-contrast-high');
    if (s.reduceMotion) root.classList.add('a11y-reduce-motion');
    if (s.focusVisible) root.classList.add('a11y-focus-visible');
    if (s.underlineLinks) root.classList.add('a11y-underline-links');
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {}
    applyToDocument(settings);
  }, [settings, applyToDocument]);

  const setSettings = useCallback((next) => {
    setSettingsState((prev) => (typeof next === 'function' ? next(prev) : { ...prev, ...next }));
  }, []);

  const reset = useCallback(() => setSettingsState(defaultSettings), []);

  return (
    <AccessibilityContext.Provider value={{ settings, setSettings, reset }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility must be used within AccessibilityProvider');
  return ctx;
}
